import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Page";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ProductDialog } from "@/pages/Products";

export function CreateEpdDialog({ open, onOpenChange, onCreated }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [prodOpen, setProdOpen] = useState(false);

  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data));
  useEffect(() => { if (open) { loadProducts(); setProductId(""); setName(""); } }, [open]);

  const create = async (mode = "REAL") => {
    try {
      const { data } = await api.post("/epds", { product_id: productId, name, project_mode: mode });
      toast.success(mode === "TEST" ? "Test EPD created" : "EPD project created");
      onOpenChange(false);
      onCreated && onCreated(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create EPD project</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Product *</Label>
              <button className="text-xs text-primary hover:underline" data-testid="new-product-inline" onClick={() => setProdOpen(true)}>+ New product</button>
            </div>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="epd-product-select" className="mt-1"><SelectValue placeholder="Select a product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.category === "cement" ? "Cement" : "Ready-Mix"}</SelectItem>)}
              </SelectContent>
            </Select>
            {products.length === 0 && <p className="text-xs text-muted-foreground mt-1">No products yet — create one first.</p>}
          </div>
          <div>
            <Label className="text-xs">EPD project name *</Label>
            <Input data-testid="epd-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="2026 Product EPD" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" data-testid="epd-create-test" onClick={() => create("TEST")} disabled={!productId || !name}>Create Test EPD</Button>
          <Button data-testid="epd-create-submit" onClick={() => create("REAL")} disabled={!productId || !name}>Create &amp; start</Button>
        </DialogFooter>
      </DialogContent>
      <ProductDialog open={prodOpen} onOpenChange={setProdOpen} onSaved={() => { loadProducts(); }} />
    </Dialog>
  );
}

export default function Epds() {
  const nav = useNavigate();
  const [epds, setEpds] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/epds").then(({ data }) => setEpds(data));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="EPDs" subtitle="Environmental Product Declaration projects and versions."
        action={<Button data-testid="create-epd-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Create EPD</Button>} />
      {epds && epds.length === 0 ? (
        <Empty icon={FileText} title="No EPD projects" hint="Start an EPD from one of your products."
          action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Create EPD</Button>} />
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left">
              <th className="px-5 py-2.5">EPD project</th><th className="px-5 py-2.5">Product</th>
              <th className="px-5 py-2.5">Plant</th><th className="px-5 py-2.5">Category</th>
              <th className="px-5 py-2.5">Completion</th><th className="px-5 py-2.5">Status</th>
              <th className="px-5 py-2.5 text-right">Action</th>
            </tr></thead>
            <tbody>
              {(epds || []).map((e) => (
                <tr key={e.id} data-testid={`epd-list-row-${e.id}`} className="border-b last:border-0">
                  <td className="px-5 py-3 font-medium">{e.name}{e.is_demo && <span className="ml-2 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO</span>}</td>
                  <td className="px-5 py-3 text-slate-600">{e.product_name}</td>
                  <td className="px-5 py-3 text-slate-600">{e.plant_name}</td>
                  <td className="px-5 py-3 text-slate-600">{e.category === "cement" ? "Cement" : "Ready-Mix"}</td>
                  <td className="px-5 py-3 font-mono text-xs">{e.data_completion}%</td>
                  <td className="px-5 py-3"><StatusBadge status={e.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`open-epd-${e.id}`} onClick={() => nav(`/app/epds/${e.id}`)}>
                      Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateEpdDialog open={open} onOpenChange={setOpen} onCreated={(id) => nav(`/app/epds/${id}`)} />
    </div>
  );
}
